import { useCallback, useEffect, useRef, useState } from 'react';

export function useCanvasInteraction() {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const canvasShellRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  useEffect(() => {
    const onMouseUp = () => { dragRef.current.active = false; };
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  // Space key for explicit pan mode (like Figma/Photoshop)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'TEXTAREA' || tag === 'INPUT') return;
        e.preventDefault();
        setSpaceHeld(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpaceHeld(false);
        dragRef.current.active = false;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useEffect(() => {
    const onFullScreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullScreenChange);
  }, []);

  const fitToView = useCallback(() => {
    const viewport = viewportRef.current;
    const stage = stageRef.current;
    if (!viewport || !stage) return;

    const svg = stage.querySelector('svg');
    if (!svg) return;

    const viewBox = svg.viewBox?.baseVal;
    const width = viewBox?.width || parseFloat(svg.getAttribute('width') || '0') || 1200;
    const height = viewBox?.height || parseFloat(svg.getAttribute('height') || '0') || 800;

    const targetWidth = viewport.clientWidth - 48;
    const targetHeight = viewport.clientHeight - 48;
    const nextZoom = Math.max(0.15, Math.min(4, Math.min(targetWidth / width, targetHeight / height)));

    setZoom(nextZoom);
    setPan({
      x: (viewport.clientWidth - width * nextZoom) / 2,
      y: (viewport.clientHeight - height * nextZoom) / 2,
    });
  }, []);

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!viewportRef.current) return;

    const rect = viewportRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const delta = event.deltaY < 0 ? 0.12 : -0.12;

    setZoom((prev) => {
      const next = Math.max(0.15, Math.min(4, prev + delta));
      const ratio = next / prev;
      setPan((p) => ({
        x: mouseX - ratio * (mouseX - p.x),
        y: mouseY - ratio * (mouseY - p.y),
      }));
      return next;
    });
  }, []);

  const handleDragStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    dragRef.current.active = true;
    dragRef.current.startX = event.clientX;
    dragRef.current.startY = event.clientY;
    dragRef.current.originX = pan.x;
    dragRef.current.originY = pan.y;
  }, [pan]);

  const handleDragMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    setPan({
      x: dragRef.current.originX + (event.clientX - dragRef.current.startX),
      y: dragRef.current.originY + (event.clientY - dragRef.current.startY),
    });
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!canvasShellRef.current) return;
    if (!document.fullscreenElement) {
      await canvasShellRef.current.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  }, []);

  const zoomIn = useCallback(() => setZoom((v) => Math.min(4, v + 0.1)), []);
  const zoomOut = useCallback(() => setZoom((v) => Math.max(0.15, v - 0.1)), []);

  return {
    zoom, pan, isFullscreen, spaceHeld,
    viewportRef, stageRef, canvasShellRef,
    fitToView, resetView,
    handleWheel, handleDragStart, handleDragMove,
    toggleFullscreen, zoomIn, zoomOut,
  };
}
